import React, { useState, useEffect } from 'react';
import {
  DocumentArrowDownIcon,
  PrinterIcon, // Add PrinterIcon
  CalendarDaysIcon,
  BanknotesIcon,
  ChartPieIcon,
  ArrowTrendingDownIcon // Removed ArrowTrendingUpIcon import comment as it's already removed
} from '@heroicons/react/24/outline';
import axios from '../../api/axiosConfig';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';

interface ExpenseStats {
  id: number;
  category: string;
  subcategory: string;
  amount: number;
  date: string;
  method: string;
  description: string;
  createdBy: string;
}

const ExpenseReport: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [expenseStats, setExpenseStats] = useState<ExpenseStats[]>([]);
  const [period, setPeriod] = useState('month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  // Effect to fetch data when period, dates, sorting, or category changes
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      let startToFetch = startDate;
      let endToFetch = endDate;
      let updateStateDates = false;

      if (period !== 'custom') {
        const today = new Date();
        const calculatedStart = new Date();
        if (period === 'month') {
          calculatedStart.setMonth(today.getMonth() - 1);
        } else if (period === 'quarter') {
          calculatedStart.setMonth(today.getMonth() - 3);
        } else if (period === 'year') {
          calculatedStart.setFullYear(today.getFullYear() - 1);
        }
        const calculatedStartDateStr = calculatedStart.toISOString().split('T')[0];
        const calculatedEndDateStr = today.toISOString().split('T')[0];

        startToFetch = calculatedStartDateStr;
        endToFetch = calculatedEndDateStr;
        if (startDate !== calculatedStartDateStr || endDate !== calculatedEndDateStr) {
          updateStateDates = true;
        }
      }

      if (!startToFetch || !endToFetch) {
         if (period === 'custom') {
          console.log("Custom period selected but dates not set, defaulting to 'month'");
          setPeriod('month'); // Default to month if custom dates are invalid
          setLoading(false);
          return;
        }
        console.error("Invalid date range for fetch:", startToFetch, endToFetch);
        setLoading(false);
        setExpenseStats([]);
        return;
      }

      try {
        const response = await axios.get('/reports/expenses', {
          params: {
            startDate: startToFetch,
            endDate: endToFetch,
            sortBy,
            sortOrder,
            category: categoryFilter !== 'all' ? categoryFilter : undefined
          }
        });
        setExpenseStats(response.data.expenses || []);
        if (updateStateDates) {
          setStartDate(startToFetch);
          setEndDate(endToFetch);
        }
      } catch (error: any) {
        console.error('Error fetching expense report data:', error);
        setExpenseStats([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [period, sortBy, sortOrder, categoryFilter]); // Dependencies are already correct
  
  const handlePeriodChange = (newPeriod: string) => {
    setPeriod(newPeriod);
    setStartDate('');
    setEndDate('');
  };
  
  const handleDateRangeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Update state, useEffect will trigger fetch
    if (startDate && endDate) {
      setPeriod('custom' + startDate + endDate);
    }
  };

  // Removed separate fetchExpenseStats function
  
  const handleSortChange = (column: string) => {
    if (sortBy === column) {
      // Toggle sort order if the same column
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new sort column and default to descending
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  // Open printable view in a new tab
  const handleOpenPrintView = () => {
    if (!startDate || !endDate) {
      alert('Пожалуйста, выберите или примените период для печати отчета.');
      return;
    }
    // Construct the URL for the print page with query parameters
    const params = new URLSearchParams({ startDate, endDate });
    if (categoryFilter !== 'all') {
      params.append('category', categoryFilter);
    }
    const printUrl = `/reports/expenses/print?${params.toString()}`;
    // Open the URL in a new tab
    const printWindow = window.open(printUrl, '_blank');
    if (!printWindow) {
      alert('Не удалось открыть окно для печати. Пожалуйста, проверьте настройки блокировки всплывающих окон.');
    }
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ru-KZ', {
      style: 'currency',
      currency: 'KZT',
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  // Calculate totals
  const totalExpenses = expenseStats.reduce((sum, expense) => sum + expense.amount, 0);
  
  // Get unique categories for filter
  const uniqueCategories = Array.from(new Set(expenseStats.map(expense => expense.category)));
  
  // Format date
  const formatDate = (dateString: string) => {
    if (!dateString) return 'Нет данных';
    return new Date(dateString).toLocaleDateString('ru-KZ');
  };
  
  // Get average expense per day
  const daysDiff = startDate && endDate ? 
    Math.round(Math.abs(new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24)) : 30;
  const avgPerDay = totalExpenses / (daysDiff || 30);
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  return (
    <div>
      <PageHeader
        title="Отчет по расходам"
        subtitle={`Статистика расходов за период: ${startDate ? new Date(startDate).toLocaleDateString('ru-KZ') : '?'} - ${endDate ? new Date(endDate).toLocaleDateString('ru-KZ') : '?'}`}
        actions={
          <button
            onClick={handleOpenPrintView} // Use new handler
            className="btn-secondary flex items-center"
            disabled={!startDate || !endDate} // Disable if dates aren't set
          >
            <PrinterIcon className="h-5 w-5 mr-1" /> {/* Use PrinterIcon */}
            Печать
          </button>
        }
      />
      
      {/* Period selection */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4 justify-between mb-4">
          <div className="flex space-x-2">
            <button
              className={`px-3 py-1 rounded ${period === 'month' ? 'bg-primary text-white' : 'bg-gray-100'}`}
              onClick={() => handlePeriodChange('month')}
            >
              Месяц
            </button>
            <button
              className={`px-3 py-1 rounded ${period === 'quarter' ? 'bg-primary text-white' : 'bg-gray-100'}`}
              onClick={() => handlePeriodChange('quarter')}
            >
              Квартал
            </button>
            <button
              className={`px-3 py-1 rounded ${period === 'year' ? 'bg-primary text-white' : 'bg-gray-100'}`}
              onClick={() => handlePeriodChange('year')}
            >
              Год
            </button>
          </div>
          
          <div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="form-input"
            >
              <option value="all">Все категории</option>
              {uniqueCategories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
        </div>
        
        <form onSubmit={handleDateRangeSubmit} className="flex flex-col sm:flex-row gap-2 items-end">
          <div className="w-full sm:w-auto">
            <label className="block text-sm font-medium text-gray-700 mb-1">Начальная дата</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="form-input"
              required
            />
          </div>
          <div className="w-full sm:w-auto">
            <label className="block text-sm font-medium text-gray-700 mb-1">Конечная дата</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="form-input"
              required
            />
          </div>
          <button type="submit" className="btn-primary flex items-center">
            <CalendarDaysIcon className="h-5 w-5 mr-1" />
            Применить
          </button>
        </form>
      </div>
      
      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Общие расходы</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalExpenses)}</p>
            </div>
            <div className="bg-red-100 p-3 rounded-full">
              <BanknotesIcon className="h-6 w-6 text-red-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Средний расход / день</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(avgPerDay)}</p>
            </div>
            <div className="bg-orange-100 p-3 rounded-full">
              <ArrowTrendingDownIcon className="h-6 w-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>
      
      {/* Expense table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSortChange('date')}
                >
                  Дата
                  {sortBy === 'date' && (
                    <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSortChange('category')}
                >
                  Категория
                  {sortBy === 'category' && (
                    <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSortChange('subcategory')}
                >
                  Подкатегория
                  {sortBy === 'subcategory' && (
                    <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSortChange('amount')}
                >
                  Сумма
                  {sortBy === 'amount' && (
                    <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSortChange('method')}
                >
                  Способ оплаты
                  {sortBy === 'method' && (
                    <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSortChange('description')}
                >
                  Описание
                  {sortBy === 'description' && (
                    <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSortChange('createdBy')}
                >
                  Кем создан
                  {sortBy === 'createdBy' && (
                    <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {expenseStats.length > 0 ? (
                expenseStats.map((expense) => (
                  <tr key={expense.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatDate(expense.date)}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {expense.category}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {expense.subcategory || '—'}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-red-600">
                      {formatCurrency(expense.amount)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {expense.method}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs " title={expense.description}>
                      {expense.description}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {expense.createdBy}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500">
                    Нет данных для отображения
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ExpenseReport;
